import {
    Box,
    useTheme,
    Button,
    Dialog,
    Typography,
    IconButton,
    styled,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import moment from 'moment'
import { Avatar } from 'packages/eid-ui'
import { getFullImageUrl } from 'utils'
import RecordingsTab from 'containers/ManageAccess/Details/Computers/RecordingTab'
import {
    useAccessRequestPolicy,
    useCurrentPerson,
    useHasAccessToViewPSMRecordings,
} from 'hooks'
import useSubcomponents from 'useSubcomponents'
import { MOBILE_SCREEN_WIDTH } from 'packages/core/utils/constants'
import RemainingTime from 'components/RemainingTime'

const mobileScreenWidth = '767px'

const hasAccessToJustificationCommentControl =
    'ITShop-Computers-Details-JustificationComment-Control'

const StyledSectionHeader = styled(Box)(({ theme }) => ({
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    color: `${theme?.palette?.common?.black} !important`,
    overflow: 'hidden',
    lineHeight: '15px',
    fontSize: '14px',
    padding: '15px 31px',
    borderTop: '1px solid rgba(0, 0, 0, .05)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    fontWeight: 'bold',
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '8.00px 8.00px',
    '& svg': {
        margin: '0px !important',
    },
}))
const StyledBodyInner = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    marginBottom: '16px',
    [`@media (max-width:${MOBILE_SCREEN_WIDTH - 1}px)`]: {
        display: 'block',
    },
})
const StyledBodyLeftInner = styled(Box)({
    maxWidth: '232px',
    width: '100%',
})
const StyledBodyLeftInnerTitle = styled(Box)({
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#9b9b9b',
    margin: 0,
})
const StyledBodyLeftInnerResult = styled(Box)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 500,
    color: theme?.palette?.common?.black,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
}))

export interface ComputerSessionRequestDetailsProps {
    item: any
    open: boolean
    onClose: () => void
    title: string
    handleTerminate: () => void
}

export const ComputerSessionRequestDetails: FC<
    ComputerSessionRequestDetailsProps
> = (props) => {
    const { open, onClose, title, item, handleTerminate } = props

    const theme = useTheme()

    const { t } = useTranslation()

    const ComputersConnectedWhenAndDuration = (name: string, data: any) => {
        const { t } = useTranslation()
        const whenData = data[name]
        let connectedWhen = `0 ${t('min')}`
        if (whenData?.days > 0) {
            connectedWhen = `${whenData.days} ${
                whenData.days > 1 ? t('days') : t('day')
            }`
        }
        if (whenData?.hours > 0) {
            connectedWhen =
                connectedWhen === `0 ${t('min')}`
                    ? `${whenData.hours} ${
                          whenData.hours > 1 ? t('hours') : t('hour')
                      }`
                    : connectedWhen +
                      ` ${whenData.hours} ${
                          whenData.hours > 1 ? t('hours') : t('hour')
                      }`
        }
        if (whenData?.minutes > 0) {
            connectedWhen =
                connectedWhen === `0 ${t('min')}`
                    ? `${whenData.minutes} ${
                          whenData.minutes > 1 ? t('mins') : t('min')
                      }`
                    : connectedWhen +
                      ` ${whenData.minutes} ${
                          whenData.minutes > 1 ? t('mins') : t('min')
                      }`
        }
        return <>{connectedWhen}</>
    }

    const accessRequestPolicy = useAccessRequestPolicy(item.requestPolicyId)
    const { data: currentPerson } = useCurrentPerson()
    const isSelf = currentPerson.id === item.checkedOutByPersonId

    const { canSeeComputersPSMSessionsRecordings, hasAccessToControl } =
        useSubcomponents()

    const canSeeRecordedSession =
        canSeeComputersPSMSessionsRecordings &&
        !item.canTerminateSession &&
        item.recordingUploaded

    const canSeeLiveSession =
        canSeeComputersPSMSessionsRecordings &&
        item.canTerminateSession &&
        accessRequestPolicy?.allowLiveSessionSnooping

    const canSeeRecordings = canSeeRecordedSession || canSeeLiveSession

    const [showRecordings, setShowRecordings] = useState(
        isSelf && canSeeRecordings,
    )

    const {
        data: accessToViewSomeoneElseRecordings,
        isInitialLoading: isInitialLoadingRecordingsAccess,
    } = useHasAccessToViewPSMRecordings(
        item.checkedOutByPersonId,
        !isSelf && canSeeRecordings,
    )

    useEffect(() => {
        if (accessToViewSomeoneElseRecordings?.hasAccess && canSeeRecordings) {
            setShowRecordings(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToViewSomeoneElseRecordings, isInitialLoadingRecordingsAccess])

    return (
        <Dialog
            scroll={'body'}
            open={open}
            onClose={onClose}
            aria-labelledby={title}
            aria-describedby={title}
            PaperProps={{
                sx: {
                    maxWidth: '1000px',
                    width: '840px',
                    [`@media (max-width:${mobileScreenWidth})`]: {
                        margin: '64px 16px',
                        width: 'calc(100% - 32px) !important',
                        maxWidth: 'calc(100% - 32px) !important ',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    height: '80px',
                    alignItems: 'center',
                    padding: '16px 12px 16px 32px',
                    backgroundColor: theme?.palette?.background?.paper,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#3b454d',
                    }}
                >
                    {title ? title : '-'}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                    }}
                >
                    <IconButton onClick={onClose} size="large">
                        <Icon name="Close" color="#959598" />
                    </IconButton>
                </Box>
            </Box>
            <StyledSectionHeader>
                {t('Common_SessionHistoryDetails')}
            </StyledSectionHeader>
            <Box
                sx={{
                    backgroundColor: '#fbfbfd',
                    padding: '24px 40px',
                    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',

                    '&:focus': {
                        outline: 'none',
                    },
                }}
            >
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_ComputerId')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.computerId ? item.computerId : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_Credentials')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.externalCredentialFriendlyName
                                ? item.externalCredentialFriendlyName
                                : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_ConnectedPersonName')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            <Avatar
                                size="small"
                                src={getFullImageUrl(
                                    item.checkedOutByPersonThumbUrl,
                                )}
                            />
                            <span
                                style={{
                                    marginLeft: '5px',
                                }}
                            >
                                {item.checkedOutByPersonName
                                    ? item.checkedOutByPersonName
                                    : '-'}
                            </span>
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_ConnectedPersonLogin')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.checkedOutByPersonName
                                ? item.checkedOutByPersonName
                                : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_ConnectedPersonId')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.checkedOutByPersonId
                                ? item.checkedOutByPersonId
                                : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_StartTime')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.startTimeUtc
                                ? moment
                                      .utc(item.startTimeUtc)
                                      .local()
                                      .format('L LT')
                                : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_EndTime')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.endTimeUtc
                                ? moment
                                      .utc(item.endTimeUtc)
                                      .local()
                                      .format('L LT')
                                : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                {item.canTerminateSession ? (
                    <StyledBodyInner>
                        <StyledBodyLeftInner>
                            <StyledBodyLeftInnerTitle as="h2">
                                {t('RemainingTime')}
                            </StyledBodyLeftInnerTitle>
                        </StyledBodyLeftInner>
                        <Box>
                            <StyledBodyLeftInnerResult as="h2">
                                <RemainingTime
                                    endTime={item.credCheckedOutEndTimeUtc}
                                />
                            </StyledBodyLeftInnerResult>
                        </Box>
                    </StyledBodyInner>
                ) : (
                    <StyledBodyInner>
                        <StyledBodyLeftInner>
                            <StyledBodyLeftInnerTitle as="h2">
                                {t('Common_ConnectedWhen')}
                            </StyledBodyLeftInnerTitle>
                        </StyledBodyLeftInner>
                        <Box>
                            <StyledBodyLeftInnerResult as="h2">
                                {ComputersConnectedWhenAndDuration(
                                    'when',
                                    item,
                                )}
                            </StyledBodyLeftInnerResult>
                        </Box>
                    </StyledBodyInner>
                )}

                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_SharedCredentialsId')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.externalCredentialId
                                ? item.externalCredentialId
                                : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_RDPSession')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.isRdp ? t('Common_Yes') : t('Common_No')}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_TerminateSession')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.canTerminateSession
                                ? t('Common_Yes')
                                : t('Common_No')}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                <StyledBodyInner>
                    <StyledBodyLeftInner>
                        <StyledBodyLeftInnerTitle as="h2">
                            {t('Common_LOA')}
                        </StyledBodyLeftInnerTitle>
                    </StyledBodyLeftInner>
                    <Box>
                        <StyledBodyLeftInnerResult as="h2">
                            {item.assuranceLevel ? item.assuranceLevel : '-'}
                        </StyledBodyLeftInnerResult>
                    </Box>
                </StyledBodyInner>
                {hasAccessToControl(hasAccessToJustificationCommentControl) && (
                    <StyledBodyInner
                        data-protectedsubcomponent={
                            hasAccessToJustificationCommentControl
                        }
                    >
                        <StyledBodyLeftInner>
                            <StyledBodyLeftInnerTitle as="h2">
                                {t('JustificationComment')}
                            </StyledBodyLeftInnerTitle>
                        </StyledBodyLeftInner>
                        <Box>
                            <StyledBodyLeftInnerResult as="h2">
                                {item.comment ? item.comment : '-'}
                            </StyledBodyLeftInnerResult>
                        </Box>
                    </StyledBodyInner>
                )}
            </Box>
            {showRecordings && (
                <>
                    <StyledSectionHeader
                        data-protectedsubcomponent={
                            canSeeComputersPSMSessionsRecordings
                        }
                    >
                        {t('Common_Recording')}
                    </StyledSectionHeader>
                    <Box
                        data-protectedsubcomponent={
                            canSeeComputersPSMSessionsRecordings
                        }
                        sx={{
                            backgroundColor: '#fbfbfd',
                            padding: '24px 40px',
                            boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',

                            '&:focus': {
                                outline: 'none',
                            },
                        }}
                    >
                        <RecordingsTab item={item} />
                    </Box>
                </>
            )}
            {item.canTerminateSession ? (
                <Box
                    sx={{
                        '& button': {
                            background: theme?.palette?.primary?.main,
                            '&:hover': {
                                background: theme?.palette?.primary?.main,
                            },
                        },
                    }}
                >
                    <Box display="flex" width="100%" padding="24px 32px">
                        <Box minWidth="167px">
                            <Button
                                onClick={handleTerminate}
                                style={{ padding: '14px 24px' }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    marginLeft="10px"
                                >
                                    <Typography
                                        style={{
                                            color: theme?.palette?.common
                                                ?.white,
                                            fontSize: '16px',
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {t('Common_Disconnect')}
                                    </Typography>
                                </Box>
                            </Button>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <></>
            )}
        </Dialog>
    )
}

export default ComputerSessionRequestDetails
